// Projects page — 3-tier tab bar (groups → items → subitems) above a phase-board/kanban view.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Gantt, Task as GanttTask, ViewMode } from "gantt-task-react";
import "gantt-task-react/dist/index.css";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Empty, Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";
import { ContextMenu, type CtxItem } from "../../shell/SubNav";

type Project = {
  id: number; code: string | null; name: string; description: string | null;
  status: string; start_date: string | null; end_date: string | null;
  owner_id: number | null; is_private: boolean; is_template: boolean;
  member_ids: number[];
};
type Task = {
  id: number; project_id: number | null; title: string; status: string;
  priority: string; due_date: string | null; assignee_ids: number[];
};
type ForumPost = {
  id: number; category: string; title: string; body_md: string;
  author_id: number | null; is_anonymous: boolean; comment_count: number; created_at: string;
};
type SubItem = { id: string; label: string };
type SubnavItem = { id: string; label: string; items?: SubItem[] };
type SubnavGroup = { id: string; title: string; items: SubnavItem[] };

const STATUSES = ["TODO", "IN_PROGRESS", "REVIEW", "DONE", "BLOCKED"] as const;
const MANAGE_GROUP_ID = "manage";
const MANAGE_FILTERS = ["all", "mine", "active", "archived"];

export default function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = Number(searchParams.get("project")) || null;
  const groupParam = searchParams.get("g") || MANAGE_GROUP_ID;
  const itemParam = searchParams.get("filter") || "";
  const subParam = searchParams.get("sub") || "";

  const me = useAuth(s => s.user);
  const authManifest = useAuth(s => s.manifest);
  const isSuperAdmin = !!(
    authManifest?.roles.includes("SUPER_ADMIN") ||
    me?.roles.includes("SUPER_ADMIN")
  );

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [viewMode, setViewMode] = useState<"kanban" | "gantt">("kanban");
  const [groups, setGroups] = useState<SubnavGroup[] | null>(null);

  const canEdit = isSuperAdmin || (project && me?.id === project.owner_id);

  const reloadGroups = () => {
    if (!projectId) { setGroups(null); return; }
    api.get<SubnavGroup[]>(`/projects/${projectId}/subnav`)
      .then(r => setGroups(r.data))
      .catch(() => setGroups([]));
  };
  useEffect(() => { reloadGroups(); /* eslint-disable-next-line */ }, [projectId]);

  useEffect(() => {
    if (!projectId) { setProject(null); return; }
    setLoading(true);
    api.get<Project>(`/projects/${projectId}`)
      .then(r => setProject(r.data))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) { setTasks([]); return; }
    api.get<Task[]>(`/projects/${projectId}/tasks`).then(r => setTasks(r.data)).catch(() => setTasks([]));
  }, [projectId]);

  const currentGroup = groups?.find(g => g.id === groupParam) ?? groups?.[0] ?? null;
  const currentItem = currentGroup?.items.find(it => it.id === itemParam) ?? currentGroup?.items[0] ?? null;
  const subItems = currentItem?.items ?? [];
  const currentSub = subItems.find(s => s.id === subParam) ?? subItems[0] ?? null;
  const isManageGroup = currentGroup?.id === MANAGE_GROUP_ID;
  const isManageItem = isManageGroup && MANAGE_FILTERS.includes(currentItem?.id ?? "");

  const setTab = (gid: string, iid?: string, sid?: string) => {
    const np = new URLSearchParams(searchParams);
    np.set("g", gid);
    const g = groups?.find(x => x.id === gid);
    const targetItem = iid ?? g?.items[0]?.id ?? "";
    if (targetItem) np.set("filter", targetItem); else np.delete("filter");
    const it = g?.items.find(x => x.id === targetItem);
    const targetSub = sid ?? it?.items?.[0]?.id ?? "";
    if (targetSub) np.set("sub", targetSub); else np.delete("sub");
    setSearchParams(np, { replace: false });
  };

  const ganttTasks: GanttTask[] = tasks
    .filter(t => t.due_date)
    .map(t => {
      const end = new Date(t.due_date!);
      const start = new Date(end); start.setDate(start.getDate() - 3);
      return {
        id: String(t.id), name: t.title, start, end, type: "task",
        progress: t.status === "DONE" ? 100 : t.status === "IN_PROGRESS" ? 50 : t.status === "REVIEW" ? 80 : 0,
        styles: {
          progressColor: t.status === "DONE" ? "#22c55e" : "#3b82f6",
          progressSelectedColor: "#2563eb",
        },
      };
    });

  if (!projectId) {
    return (
      <>
        <PageHeader title="프로젝트" subtitle="좌측에서 프로젝트를 선택하거나 새로 만드세요" />
        <div style={{ padding: 32 }}>
          <div className="t-card" style={{ padding: 40, textAlign: "center" }}>
            <I.Todo size={36} />
            <h3 style={{ marginTop: 12, marginBottom: 4 }}>프로젝트를 선택해주세요</h3>
            <p style={{ color: "var(--text-3)", margin: 0 }}>
              좌측 메뉴에서 참여 중인 프로젝트를 선택하거나, <b>새 프로젝트</b> 버튼으로 새로 만드세요.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {project?.name ?? "..."}
            {project?.is_template && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: "2px 8px",
                borderRadius: 4, background: "var(--p-tint, rgba(99,102,241,0.12))",
                color: "var(--p-700, #4338ca)", letterSpacing: 0.5,
              }}>TEMPLATE</span>
            )}
          </span>
        }
        subtitle={project?.description || (loading ? "불러오는 중…" : "프로젝트 상세")}
        right={
          isManageGroup ? (
            <div style={{ display: "flex", gap: 4 }}>
              <button className="t-btn"
                style={{ fontWeight: viewMode === "kanban" ? 700 : 400, color: viewMode === "kanban" ? "var(--p-500)" : undefined }}
                onClick={() => setViewMode("kanban")}>칸반</button>
              <button className="t-btn"
                style={{ fontWeight: viewMode === "gantt" ? 700 : 400, color: viewMode === "gantt" ? "var(--p-500)" : undefined }}
                onClick={() => setViewMode("gantt")}>간트</button>
            </div>
          ) : undefined
        }
      />

      <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 12, height: "calc(100% - 80px)" }}>
        <TabBar
          projectId={projectId}
          groups={groups}
          currentGroupId={currentGroup?.id ?? ""}
          currentItemId={currentItem?.id ?? ""}
          currentSubId={currentSub?.id ?? ""}
          canEdit={!!canEdit}
          onSelectGroup={(gid) => setTab(gid)}
          onSelectItem={(iid) => currentGroup && setTab(currentGroup.id, iid)}
          onSelectSub={(sid) => currentGroup && currentItem && setTab(currentGroup.id, currentItem.id, sid)}
          onReload={reloadGroups}
        />

        <div style={{ flex: 1, overflow: "auto" }}>
          {!currentGroup && <Loading />}

          {isManageItem && project && (
            <>
              {viewMode === "kanban" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
                  {STATUSES.map(s => (
                    <div key={s} className="t-card" style={{ padding: 10, minHeight: 240 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{s}</div>
                      {tasks.filter(t => t.status === s).map(t => (
                        <div key={t.id} style={{ background: "var(--panel)", padding: 10, borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                          <div style={{ fontWeight: 600 }}>{t.title}</div>
                          <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                            <Pill kind={t.priority === "URGENT" ? "danger" : t.priority === "HIGH" ? "warn" : "muted"}>{t.priority}</Pill>
                            {t.due_date && <Pill kind="info">{t.due_date}</Pill>}
                          </div>
                        </div>
                      ))}
                      {tasks.filter(t => t.status === s).length === 0 && (
                        <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", paddingTop: 20 }}>항목 없음</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {viewMode === "gantt" && (
                <div className="t-card" style={{ overflow: "auto", padding: 0 }}>
                  {ganttTasks.length === 0 ? (
                    <Empty msg="마감일이 있는 태스크가 없습니다" />
                  ) : (
                    <Gantt tasks={ganttTasks} viewMode={ViewMode.Week} listCellWidth="200px" columnWidth={60} />
                  )}
                </div>
              )}
            </>
          )}

          {currentGroup && currentItem && !isManageItem && project && (
            <PhaseBoardView
              project={project}
              groupId={currentGroup.id}
              itemId={currentItem.id}
              subId={currentSub?.id ?? ""}
              boardLabel={
                currentSub ? `${currentItem.label} › ${currentSub.label}` : currentItem.label
              }
            />
          )}

          {currentGroup && !currentItem && (
            <div className="t-card" style={{ padding: 24 }}>
              <Empty msg="이 그룹에는 탭이 없습니다. 그룹 옆 + 버튼이나 우클릭으로 추가하세요." />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── 3-tier tab bar ─────────────────────────────────────────────────────────

function TabBar({
  projectId, groups, currentGroupId, currentItemId, currentSubId,
  canEdit, onSelectGroup, onSelectItem, onSelectSub, onReload,
}: {
  projectId: number;
  groups: SubnavGroup[] | null;
  currentGroupId: string;
  currentItemId: string;
  currentSubId: string;
  canEdit: boolean;
  onSelectGroup: (gid: string) => void;
  onSelectItem: (iid: string) => void;
  onSelectSub: (sid: string) => void;
  onReload: () => void;
}) {
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; items: CtxItem[] } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [addingItemTo, setAddingItemTo] = useState<string | null>(null);
  const [addingSubTo, setAddingSubTo] = useState<string | null>(null); // itemId
  const [addingGroup, setAddingGroup] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");

  const base = `/projects/${projectId}/subnav`;
  const currentGroup = groups?.find(g => g.id === currentGroupId);
  const currentItem = currentGroup?.items.find(it => it.id === currentItemId);

  // ─ Helpers ─
  const handleErr = (e: any, msg: string) =>
    toast.error(msg, e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "");

  // ─ Group CRUD ─
  const addGroup = async (title: string) => {
    const t = title.trim();
    if (!t) { setAddingGroup(false); setDraftLabel(""); return; }
    try {
      const r = await api.post<{ id: string }>(`${base}/groups`, { title: t });
      toast.success("그룹 추가됨");
      setAddingGroup(false); setDraftLabel("");
      onReload(); onSelectGroup(r.data.id);
    } catch (e: any) { handleErr(e, "추가 실패"); }
  };
  const renameGroup = async (groupId: string, title: string) => {
    const t = title.trim(); if (!t) { setEditing(null); return; }
    try { await api.patch(`${base}/groups/${groupId}`, { title: t }); onReload(); }
    catch (e: any) { handleErr(e, "이름 변경 실패"); }
    setEditing(null);
  };
  const deleteGroup = async (groupId: string, title: string) => {
    if (!window.confirm(`"${title}" 그룹과 그 안의 모든 탭/세부탭을 삭제하시겠습니까?`)) return;
    try { await api.delete(`${base}/groups/${groupId}`); toast.success("그룹 삭제됨"); onReload(); }
    catch (e: any) { handleErr(e, "삭제 실패"); }
  };
  const moveGroup = async (groupId: string, dir: "left" | "right") => {
    if (!groups) return;
    const idx = groups.findIndex(g => g.id === groupId);
    const swap = dir === "left" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= groups.length) return;
    const reordered = [...groups]; [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    try { await api.put(`${base}/groups/reorder`, { ids: reordered.map(g => g.id) }); onReload(); }
    catch { toast.error("순서 변경 실패"); }
  };

  // ─ Item CRUD ─
  const addItem = async (groupId: string, label: string) => {
    const t = label.trim(); if (!t) { setAddingItemTo(null); setDraftLabel(""); return; }
    try {
      const r = await api.post<{ id: string }>(`${base}/groups/${groupId}/items`, { label: t });
      toast.success("탭 추가됨");
      setAddingItemTo(null); setDraftLabel("");
      onReload(); onSelectItem(r.data.id);
    } catch (e: any) { handleErr(e, "추가 실패"); }
  };
  const renameItem = async (gid: string, iid: string, label: string) => {
    const t = label.trim(); if (!t) { setEditing(null); return; }
    try { await api.patch(`${base}/groups/${gid}/items/${iid}`, { label: t }); onReload(); }
    catch (e: any) { handleErr(e, "이름 변경 실패"); }
    setEditing(null);
  };
  const deleteItem = async (gid: string, iid: string, label: string) => {
    if (!window.confirm(`"${label}" 탭과 모든 세부탭을 삭제하시겠습니까?`)) return;
    try { await api.delete(`${base}/groups/${gid}/items/${iid}`); toast.success("탭 삭제됨"); onReload(); }
    catch (e: any) { handleErr(e, "삭제 실패"); }
  };
  const moveItem = async (gid: string, iid: string, dir: "left" | "right") => {
    const g = groups?.find(x => x.id === gid); if (!g) return;
    const idx = g.items.findIndex(it => it.id === iid);
    const swap = dir === "left" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= g.items.length) return;
    const reordered = [...g.items]; [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    try { await api.put(`${base}/groups/${gid}/items/reorder`, { ids: reordered.map(i => i.id) }); onReload(); }
    catch { toast.error("순서 변경 실패"); }
  };

  // ─ Subitem (tier 3) CRUD ─
  const addSub = async (gid: string, iid: string, label: string) => {
    const t = label.trim(); if (!t) { setAddingSubTo(null); setDraftLabel(""); return; }
    try {
      const r = await api.post<{ id: string }>(`${base}/groups/${gid}/items/${iid}/subs`, { label: t });
      toast.success("세부탭 추가됨");
      setAddingSubTo(null); setDraftLabel("");
      onReload(); onSelectSub(r.data.id);
    } catch (e: any) { handleErr(e, "추가 실패"); }
  };
  const renameSub = async (gid: string, iid: string, sid: string, label: string) => {
    const t = label.trim(); if (!t) { setEditing(null); return; }
    try { await api.patch(`${base}/groups/${gid}/items/${iid}/subs/${sid}`, { label: t }); onReload(); }
    catch (e: any) { handleErr(e, "이름 변경 실패"); }
    setEditing(null);
  };
  const deleteSub = async (gid: string, iid: string, sid: string, label: string) => {
    if (!window.confirm(`"${label}" 세부탭을 삭제하시겠습니까?`)) return;
    try { await api.delete(`${base}/groups/${gid}/items/${iid}/subs/${sid}`); toast.success("세부탭 삭제됨"); onReload(); }
    catch (e: any) { handleErr(e, "삭제 실패"); }
  };
  const moveSub = async (gid: string, iid: string, sid: string, dir: "left" | "right") => {
    const it = groups?.find(g => g.id === gid)?.items.find(x => x.id === iid); if (!it) return;
    const subs = it.items ?? [];
    const idx = subs.findIndex(s => s.id === sid);
    const swap = dir === "left" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= subs.length) return;
    const reordered = [...subs]; [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    try { await api.put(`${base}/groups/${gid}/items/${iid}/subs/reorder`, { ids: reordered.map(s => s.id) }); onReload(); }
    catch { toast.error("순서 변경 실패"); }
  };

  // ─ Render ─
  if (!groups) return <div style={{ padding: 8, fontSize: 12, color: "var(--text-3)" }}>탭 불러오는 중…</div>;

  const editInputStyle: React.CSSProperties = {
    padding: "4px 8px", fontSize: 13, border: "1px solid var(--p-400)", borderRadius: 4, outline: "none",
  };

  const addBtnStyle: React.CSSProperties = {
    border: "1px dashed var(--border-strong, #d1d5db)",
    background: "transparent", color: "var(--text-3)",
    cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
  };

  return (
    <div className="t-card" style={{ padding: 0, overflow: "hidden" }}>
      {/* Tier 1 — groups */}
      <div style={{ display: "flex", alignItems: "stretch", borderBottom: "1px solid var(--border)", background: "var(--panel)" }}>
        {groups.map((g, gIdx) => {
          const active = g.id === currentGroupId;
          const editingThis = editing === `g/${g.id}`;
          return (
            <div key={g.id}
              onContextMenu={(e) => {
                if (!canEdit) return;
                e.preventDefault();
                setCtxMenu({ x: e.clientX, y: e.clientY, items: [
                  { label: "이름 변경", icon: <I.Edit size={12}/>, onClick: () => setEditing(`g/${g.id}`) },
                  { label: "탭 추가", icon: <I.Plus size={12}/>, onClick: () => { onSelectGroup(g.id); setAddingItemTo(g.id); } },
                  { sep: true, label: "", onClick: () => {} },
                  ...(gIdx > 0 ? [{ label: "왼쪽으로", icon: <span>←</span>, onClick: () => moveGroup(g.id, "left") }] : []),
                  ...(gIdx < groups.length - 1 ? [{ label: "오른쪽으로", icon: <span>→</span>, onClick: () => moveGroup(g.id, "right") }] : []),
                  { sep: true, label: "", onClick: () => {} },
                  { label: "그룹 삭제", icon: <I.Trash size={12}/>, danger: true, onClick: () => deleteGroup(g.id, g.title) },
                ]});
              }}
              style={{ display: "flex", alignItems: "center" }}
            >
              {editingThis ? (
                <input autoFocus defaultValue={g.title}
                  onKeyDown={(e) => { if (e.key === "Enter") renameGroup(g.id, (e.target as HTMLInputElement).value); if (e.key === "Escape") setEditing(null); }}
                  onBlur={(e) => renameGroup(g.id, e.target.value)}
                  style={{ ...editInputStyle, margin: 6, fontSize: 14 }}/>
              ) : (
                <button onClick={() => onSelectGroup(g.id)}
                  title={canEdit ? "마우스 오른쪽 클릭으로 편집" : g.title}
                  style={{
                    padding: "10px 16px", border: "none",
                    background: active ? "var(--bg-0, #fff)" : "transparent",
                    color: active ? "var(--p-600, #4f46e5)" : "var(--text-2)",
                    fontWeight: active ? 700 : 500, fontSize: 14, cursor: "pointer",
                    borderBottom: active ? "2px solid var(--p-500)" : "2px solid transparent",
                    marginBottom: -1,
                  }}
                >{g.title}</button>
              )}
            </div>
          );
        })}
        {canEdit && (
          addingGroup ? (
            <input autoFocus value={draftLabel} placeholder="그룹 이름"
              onChange={e => setDraftLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addGroup(draftLabel); if (e.key === "Escape") { setAddingGroup(false); setDraftLabel(""); } }}
              onBlur={() => addGroup(draftLabel)}
              style={{ ...editInputStyle, margin: 6, fontSize: 14 }}/>
          ) : (
            <button onClick={() => { setAddingGroup(true); setDraftLabel(""); }} title="그룹 추가"
              style={{ padding: "10px 12px", border: "none", background: "transparent", color: "var(--text-3)", cursor: "pointer", display: "flex", alignItems: "center" }}
              onMouseOver={e => (e.currentTarget.style.color = "var(--p-500)")}
              onMouseOut={e => (e.currentTarget.style.color = "var(--text-3)")}>
              <I.Plus size={14}/>
            </button>
          )
        )}
      </div>

      {/* Tier 2 — items */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", padding: "6px 8px", gap: 4, background: "var(--bg-0, #fff)", borderBottom: subItemsExist(currentItem) ? "1px solid var(--border)" : undefined }}>
        {currentGroup?.items.map((it, iIdx) => {
          const active = it.id === currentItemId;
          const editingThis = editing === `i/${it.id}`;
          const isFirst = iIdx === 0;
          const isLast = iIdx === (currentGroup.items.length - 1);
          if (editingThis) {
            return (
              <input key={it.id} autoFocus defaultValue={it.label}
                onKeyDown={(e) => { if (e.key === "Enter") renameItem(currentGroup.id, it.id, (e.target as HTMLInputElement).value); if (e.key === "Escape") setEditing(null); }}
                onBlur={(e) => renameItem(currentGroup.id, it.id, e.target.value)}
                style={editInputStyle}/>
            );
          }
          return (
            <button key={it.id} onClick={() => onSelectItem(it.id)}
              onContextMenu={(e) => {
                if (!canEdit) return;
                e.preventDefault();
                setCtxMenu({ x: e.clientX, y: e.clientY, items: [
                  { label: "이름 변경", icon: <I.Edit size={12}/>, onClick: () => setEditing(`i/${it.id}`) },
                  { label: "세부탭 추가", icon: <I.Plus size={12}/>, onClick: () => { onSelectItem(it.id); setAddingSubTo(it.id); } },
                  { sep: true, label: "", onClick: () => {} },
                  ...(!isFirst ? [{ label: "왼쪽으로", icon: <span>←</span>, onClick: () => moveItem(currentGroup.id, it.id, "left") }] : []),
                  ...(!isLast ? [{ label: "오른쪽으로", icon: <span>→</span>, onClick: () => moveItem(currentGroup.id, it.id, "right") }] : []),
                  { sep: true, label: "", onClick: () => {} },
                  { label: "이 탭 삭제", icon: <I.Trash size={12}/>, danger: true, onClick: () => deleteItem(currentGroup.id, it.id, it.label) },
                ]});
              }}
              title={canEdit ? "마우스 오른쪽 클릭으로 편집" : it.label}
              style={{
                padding: "5px 12px",
                border: "1px solid " + (active ? "var(--p-500, #6366f1)" : "var(--border)"),
                background: active ? "var(--p-tint, rgba(99,102,241,0.08))" : "transparent",
                color: active ? "var(--p-700, #4338ca)" : "var(--text-2)",
                fontWeight: active ? 600 : 400, fontSize: 13,
                borderRadius: 14, cursor: "pointer",
              }}>{it.label}</button>
          );
        })}

        {addingItemTo === currentGroup?.id ? (
          <input autoFocus value={draftLabel} placeholder="새 탭 이름"
            onChange={e => setDraftLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && currentGroup) addItem(currentGroup.id, draftLabel); if (e.key === "Escape") { setAddingItemTo(null); setDraftLabel(""); } }}
            onBlur={() => { if (currentGroup) addItem(currentGroup.id, draftLabel); }}
            style={{ ...editInputStyle, borderRadius: 14, minWidth: 100 }}/>
        ) : canEdit && currentGroup ? (
          <button onClick={() => { setAddingItemTo(currentGroup.id); setDraftLabel(""); }} title="탭 추가"
            style={{ ...addBtnStyle, padding: "5px 10px", borderRadius: 14, fontSize: 13 }}
            onMouseOver={e => (e.currentTarget.style.color = "var(--p-500)")}
            onMouseOut={e => (e.currentTarget.style.color = "var(--text-3)")}>
            <I.Plus size={12}/> 탭
          </button>
        ) : null}

        {currentGroup && currentGroup.items.length === 0 && !addingItemTo && (
          <span style={{ fontSize: 12, color: "var(--text-3)", padding: "4px 8px" }}>
            탭이 없습니다. {canEdit ? "+ 버튼이나 우클릭으로 추가하세요." : ""}
          </span>
        )}
      </div>

      {/* Tier 3 — subitems (only if current item has any, or admin is adding) */}
      {(subItemsExist(currentItem) || addingSubTo === currentItem?.id || canEdit) && currentItem && currentGroup && (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", padding: "5px 12px", gap: 4, background: "var(--bg-0, #fff)" }}>
          <span style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginRight: 4 }}>세부</span>
          {(currentItem.items ?? []).map((s, sIdx) => {
            const active = s.id === currentSubId;
            const editingThis = editing === `s/${s.id}`;
            const isFirst = sIdx === 0;
            const isLast = sIdx === (currentItem.items!.length - 1);
            if (editingThis) {
              return (
                <input key={s.id} autoFocus defaultValue={s.label}
                  onKeyDown={(e) => { if (e.key === "Enter") renameSub(currentGroup.id, currentItem.id, s.id, (e.target as HTMLInputElement).value); if (e.key === "Escape") setEditing(null); }}
                  onBlur={(e) => renameSub(currentGroup.id, currentItem.id, s.id, e.target.value)}
                  style={{ ...editInputStyle, fontSize: 12, padding: "3px 6px" }}/>
              );
            }
            return (
              <button key={s.id} onClick={() => onSelectSub(s.id)}
                onContextMenu={(e) => {
                  if (!canEdit) return;
                  e.preventDefault();
                  setCtxMenu({ x: e.clientX, y: e.clientY, items: [
                    { label: "이름 변경", icon: <I.Edit size={12}/>, onClick: () => setEditing(`s/${s.id}`) },
                    { sep: true, label: "", onClick: () => {} },
                    ...(!isFirst ? [{ label: "왼쪽으로", icon: <span>←</span>, onClick: () => moveSub(currentGroup.id, currentItem.id, s.id, "left") }] : []),
                    ...(!isLast ? [{ label: "오른쪽으로", icon: <span>→</span>, onClick: () => moveSub(currentGroup.id, currentItem.id, s.id, "right") }] : []),
                    { sep: true, label: "", onClick: () => {} },
                    { label: "이 세부탭 삭제", icon: <I.Trash size={12}/>, danger: true, onClick: () => deleteSub(currentGroup.id, currentItem.id, s.id, s.label) },
                  ]});
                }}
                title={canEdit ? "마우스 오른쪽 클릭으로 편집" : s.label}
                style={{
                  padding: "3px 9px", fontSize: 12,
                  border: "1px solid " + (active ? "var(--p-500)" : "transparent"),
                  background: active ? "var(--p-50, #eef2ff)" : "var(--panel)",
                  color: active ? "var(--p-700)" : "var(--text-2)",
                  fontWeight: active ? 600 : 400,
                  borderRadius: 10, cursor: "pointer",
                }}>{s.label}</button>
            );
          })}

          {addingSubTo === currentItem.id ? (
            <input autoFocus value={draftLabel} placeholder="새 세부탭"
              onChange={e => setDraftLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addSub(currentGroup.id, currentItem.id, draftLabel); if (e.key === "Escape") { setAddingSubTo(null); setDraftLabel(""); } }}
              onBlur={() => addSub(currentGroup.id, currentItem.id, draftLabel)}
              style={{ ...editInputStyle, fontSize: 12, padding: "3px 6px", borderRadius: 10, minWidth: 80 }}/>
          ) : canEdit ? (
            <button onClick={() => { setAddingSubTo(currentItem.id); setDraftLabel(""); }} title="세부탭 추가"
              style={{ ...addBtnStyle, padding: "3px 8px", borderRadius: 10, fontSize: 12 }}
              onMouseOver={e => (e.currentTarget.style.color = "var(--p-500)")}
              onMouseOut={e => (e.currentTarget.style.color = "var(--text-3)")}>
              <I.Plus size={10}/> 세부
            </button>
          ) : null}
        </div>
      )}

      {ctxMenu && (
        <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={ctxMenu.items} onClose={() => setCtxMenu(null)} />
      )}
    </div>
  );
}

function subItemsExist(it: SubnavItem | undefined): boolean {
  return !!(it && it.items && it.items.length > 0);
}

// ─── Phase Board (forum-backed posts per project + sub-board) ──────────────

function PhaseBoardView({
  project, groupId, itemId, subId, boardLabel,
}: {
  project: Project; groupId: string; itemId: string; subId: string; boardLabel: string;
}) {
  const tagParts = [`proj_${project.code ?? project.id}`, groupId, itemId];
  if (subId) tagParts.push(subId);
  const tag = tagParts.join("_");

  const [posts, setPosts] = useState<ForumPost[] | null>(null);
  const [selected, setSelected] = useState<ForumPost | null>(null);
  const [showWrite, setShowWrite] = useState(false);
  const [form, setForm] = useState({ title: "", body_md: "" });
  const [commentText, setCommentText] = useState("");
  const [userMap, setUserMap] = useState<Record<number, string>>({});

  useEffect(() => {
    api.get<{ data: { id: number; name: string }[] }>("/users?size=200").then(r => {
      const m: Record<number, string> = {};
      r.data.data.forEach(u => { m[u.id] = u.name; });
      setUserMap(m);
    }).catch(() => {});
  }, []);

  const loadPosts = () => {
    setPosts(null);
    api.get<ForumPost[]>(`/forum/posts?tag=${encodeURIComponent(tag)}&limit=50`)
      .then(r => setPosts(r.data))
      .catch(() => setPosts([]));
  };

  useEffect(() => { loadPosts(); setSelected(null); setShowWrite(false); }, [tag]);

  const createPost = async () => {
    if (!form.title.trim()) return;
    try {
      await api.post("/forum/posts", { ...form, category: "FREE", tags: [tag], is_anonymous: false });
      toast.success("게시글 등록됨");
      setShowWrite(false);
      setForm({ title: "", body_md: "" });
      loadPosts();
    } catch (e: any) {
      toast.error("등록 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const openPost = async (id: number) => {
    const r = await api.get<ForumPost & { comments: { id: number; body_md: string; author_id: number | null; created_at: string }[] }>(`/forum/posts/${id}`);
    setSelected(r.data as any);
  };

  const addComment = async () => {
    if (!selected || !commentText.trim()) return;
    try {
      await api.post(`/forum/posts/${selected.id}/comments`, { body_md: commentText });
      setCommentText("");
      openPost(selected.id);
    } catch {}
  };

  const authorName = (id: number | null) => id ? (userMap[id] || `#${id}`) : "알 수 없음";

  return (
    <div style={{ display: "flex", gap: 12, height: "100%" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{boardLabel}</span>
          <span style={{ fontSize: 12, color: "var(--text-3)" }}>({project.name})</span>
          <button className="t-btn t-btn-primary" style={{ marginLeft: "auto", fontSize: 12 }}
            onClick={() => setShowWrite(v => !v)}>
            <I.Plus size={13}/> {showWrite ? "취소" : "글쓰기"}
          </button>
        </div>

        {showWrite && (
          <div className="t-card" style={{ display: "grid", gap: 10, marginBottom: 12 }}>
            <div>
              <label className="t-label">제목</label>
              <input className="t-input" value={form.title} placeholder="제목 입력"
                onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className="t-label">내용</label>
              <textarea className="t-input" rows={4} style={{ resize: "vertical" }}
                value={form.body_md} placeholder="내용을 입력하세요..."
                onChange={e => setForm({ ...form, body_md: e.target.value })} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="t-btn" onClick={() => setShowWrite(false)}>취소</button>
              <button className="t-btn t-btn-primary" onClick={createPost} disabled={!form.title.trim()}>등록</button>
            </div>
          </div>
        )}

        {posts === null && <Loading />}
        {posts?.length === 0 && <Empty msg="게시글이 없습니다. 첫 글을 남겨보세요!" />}

        <div className="t-card" style={{ padding: 0 }}>
          <div className="t-list" style={{ padding: 8 }}>
            {posts?.map(p => (
              <div key={p.id} className="t-row"
                style={{ cursor: "pointer", background: selected?.id === p.id ? "var(--p-50, #eff6ff)" : undefined }}
                onClick={() => openPost(p.id)}>
                <span className="title" style={{ flex: 1, fontSize: 13 }}>{p.title}</span>
                <span className="meta">{authorName(p.author_id)}</span>
                <span className="meta">💬 {p.comment_count}</span>
                <span className="meta">{new Date(p.created_at).toLocaleDateString("ko-KR")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selected && (
        <div style={{
          width: 380, border: "1px solid var(--border)", borderRadius: 10,
          padding: 16, display: "flex", flexDirection: "column", gap: 12,
          overflowY: "auto", background: "var(--bg-0, white)", flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 14 }}>{selected.title}</h3>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                {authorName(selected.author_id)} · {new Date(selected.created_at).toLocaleDateString("ko-KR")}
              </div>
            </div>
            <button className="t-btn" onClick={() => setSelected(null)}>✕</button>
          </div>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.7, borderBottom: "1px solid var(--border)", paddingBottom: 12 }}>
            {selected.body_md || <span style={{ color: "var(--text-3)" }}>(내용 없음)</span>}
          </div>

          <div style={{ fontWeight: 600, fontSize: 13 }}>댓글 {(selected as any).comments?.length ?? 0}개</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {((selected as any).comments ?? []).map((c: any) => (
              <div key={c.id} style={{ background: "var(--panel)", padding: "8px 10px", borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>
                  {authorName(c.author_id)} · {new Date(c.created_at).toLocaleDateString("ko-KR")}
                </div>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{c.body_md}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 6, marginTop: "auto", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
            <input className="t-input" style={{ flex: 1 }} value={commentText} placeholder="댓글 입력…"
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addComment()} />
            <button className="t-btn t-btn-primary" style={{ fontSize: 12 }} onClick={addComment}>등록</button>
          </div>
        </div>
      )}
    </div>
  );
}
